import type { PromoContext } from '../hooks/usePromoContext';

/** Pages that can host promo cards. */
export type PromoSurface = 'home' | 'search' | 'property' | 'favorites';

export interface PromoEntry {
  /** Stable id — also the localStorage dismiss key suffix and i18n sub-key. */
  id: string;
  /** Remix-icon class for the icon chip (e.g. 'ri-user-heart-line'). */
  icon: string;
  /** i18n keys (namespace 'promo'). Each has a defaultValue in en/promo.ts. */
  titleKey: string;
  bodyKey: string;
  ctaKey: string;
  /** CTA destination. A function receives the targeting context for routing. */
  href: string | ((ctx: PromoContext) => string);
  /** Which surfaces this entry may appear on. */
  surfaces: PromoSurface[];
  /** Lower shows first when more than one entry matches a surface. */
  priority: number;
  /** Relevance predicate — return false to hide for this visitor. */
  show: (ctx: PromoContext) => boolean;
  /**
   * The promoted feature isn't live yet. The card shows a "Coming soon" badge
   * and a disabled (non-navigating) CTA. `href` is ignored while true.
   */
  comingSoon?: boolean;
}

/**
 * The promo registry. Order here is incidental — selection sorts by `priority`.
 * All copy lives in i18n; this file only declares structure + targeting.
 */
export const PROMO_ENTRIES: PromoEntry[] = [
  {
    id: 'create-account',
    icon: 'ri-user-heart-line',
    titleKey: 'promo.createAccount.title',
    bodyKey: 'promo.createAccount.body',
    ctaKey: 'promo.createAccount.cta',
    href: '/register',
    surfaces: ['home', 'search', 'property', 'favorites'],
    priority: 10,
    show: (ctx) => !ctx.isAuthed,
  },
  {
    // Logged-in counterpart of create-account on the browse surfaces.
    id: 'saved-search',
    icon: 'ri-heart-add-line',
    titleKey: 'promo.savedSearch.title',
    bodyKey: 'promo.savedSearch.body',
    ctaKey: 'promo.savedSearch.cta',
    href: '/favorites',
    surfaces: ['search', 'property'],
    priority: 15,
    show: (ctx) => ctx.isAuthed,
  },
  {
    id: 'list-property',
    icon: 'ri-building-line',
    titleKey: 'promo.listProperty.title',
    bodyKey: 'promo.listProperty.body',
    ctaKey: 'promo.listProperty.cta',
    // Only non-listers see this card. Signed in, they go to the owner's listing
    // request, as every other home CTA does: /properties/new is broker-only
    // (router/config.tsx `requireBroker`) and was a dead end for them. Guests
    // register first.
    href: (ctx) => (ctx.isAuthed ? '/individual-property-request' : '/register'),
    surfaces: ['home', 'search', 'property', 'favorites'],
    priority: 20,
    show: (ctx) => !ctx.isLister,
  },
  {
    id: 'membership',
    icon: 'ri-vip-crown-2-line',
    titleKey: 'promo.membership.title',
    bodyKey: 'promo.membership.body',
    ctaKey: 'promo.membership.cta',
    href: '/membership',
    // Kept off the home band so the home trio reads like Rightmove's
    // (account · list · newsletter); still promoted on the high-intent surfaces.
    surfaces: ['search', 'property'],
    priority: 30,
    show: () => true,
  },
  {
    // Email newsletter — no signup backend yet, so this is flagged coming soon.
    id: 'newsletter',
    icon: 'ri-mail-send-line',
    titleKey: 'promo.newsletter.title',
    bodyKey: 'promo.newsletter.body',
    ctaKey: 'promo.newsletter.cta',
    href: '#',
    surfaces: ['home', 'favorites'],
    priority: 40,
    show: () => true,
    comingSoon: true,
  },
  {
    id: 'market-insights',
    icon: 'ri-line-chart-line',
    titleKey: 'promo.marketInsights.title',
    bodyKey: 'promo.marketInsights.body',
    ctaKey: 'promo.marketInsights.cta',
    // The deed-based market tools; /market-insights shows «قريباً» unless an
    // admin toggle is on.
    href: '/market-tools',
    surfaces: ['search', 'property', 'favorites'],
    priority: 45,
    show: () => true,
  },
];

/** Resolve a promo's href against the targeting context. */
export function resolvePromoHref(entry: PromoEntry, ctx: PromoContext): string {
  return typeof entry.href === 'function' ? entry.href(ctx) : entry.href;
}

/**
 * Pure selection: the entries that apply to a surface for a given context,
 * sorted by priority. Dismissal filtering happens in the component (it needs
 * browser storage). Exported separately so it can be unit-tested.
 */
export function selectPromosForSurface(
  surface: PromoSurface,
  ctx: PromoContext,
): PromoEntry[] {
  return PROMO_ENTRIES
    .filter((entry) => entry.surfaces.includes(surface) && entry.show(ctx))
    .sort((a, b) => a.priority - b.priority);
}
